/**
 * GRENADIERS - Animations & Interactions JavaScript
 * Gère les animations au scroll, carrousel témoignages, slider hero, parallaxe
 */

class GrenadiersAnimations {
  constructor() {
    this.init();
  }

  init() {
    // Attendre que le DOM soit chargé
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.initScrollReveal();
    this.initHeroSlider();
    this.initTestimonialsCarousel();
    this.initParallax();
    this.initButtonRipple();
    this.initProductCardAnimations();
  }

  /**
   * Animations au scroll - Fade-in des sections
   */
  initScrollReveal() {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          // Optionnel: arrêter d'observer après révélation pour performance
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observer tous les éléments avec la classe scroll-reveal
    document.querySelectorAll('.grenadiers-scroll-reveal').forEach(el => {
      observer.observe(el);
    });
  }

  /**
   * Slider automatique pour le hero banner
   */
  initHeroSlider() {
    const slider = document.querySelector('.grenadiers-hero-slider');
    if (!slider) return;

    const slides = slider.querySelectorAll('.grenadiers-hero-slide');
    if (slides.length <= 1) return;

    let currentSlide = 0;
    const slideInterval = 5000; // 5 secondes

    const showSlide = (index) => {
      slides.forEach((slide, i) => {
        slide.classList.toggle('active', i === index);
      });
    };

    const nextSlide = () => {
      currentSlide = (currentSlide + 1) % slides.length;
      showSlide(currentSlide);
    };

    // Démarrer le slider
    showSlide(0);
    let intervalId = setInterval(nextSlide, slideInterval);

    // Pause au hover
    slider.addEventListener('mouseenter', () => {
      if (intervalId) clearInterval(intervalId);
    });
    slider.addEventListener('mouseleave', () => {
      intervalId = setInterval(nextSlide, slideInterval);
    });
  }

  /**
   * Carrousel pour les témoignages/avis clients
   */
  initTestimonialsCarousel() {
    const carousel = document.querySelector('.grenadiers-testimonials-carousel');
    if (!carousel) return;

    const wrapper = carousel.querySelector('.grenadiers-testimonials-wrapper');
    const items = carousel.querySelectorAll('.grenadiers-testimonial-item');
    const dots = carousel.querySelectorAll('.grenadiers-carousel-dot');
    const prevBtn = carousel.querySelector('.grenadiers-carousel-arrow--prev');
    const nextBtn = carousel.querySelector('.grenadiers-carousel-arrow--next');

    if (items.length <= 1) return;

    let currentIndex = 0;
    const totalItems = items.length;

    const updateCarousel = () => {
      // Mettre à jour la position
      if (wrapper) {
        wrapper.style.transform = `translateX(-${currentIndex * 100}%)`;
      }

      // Mettre à jour les items actifs
      items.forEach((item, index) => {
        item.classList.toggle('active', index === currentIndex);
        if (index === currentIndex) {
          item.classList.add('fade-in');
        }
      });

      // Mettre à jour les dots
      dots.forEach((dot, index) => {
        dot.classList.toggle('active', index === currentIndex);
      });
    };

    const nextSlide = () => {
      currentIndex = (currentIndex + 1) % totalItems;
      updateCarousel();
    };

    const prevSlide = () => {
      currentIndex = (currentIndex - 1 + totalItems) % totalItems;
      updateCarousel();
    };

    // Événements
    if (nextBtn) nextBtn.addEventListener('click', nextSlide);
    if (prevBtn) prevBtn.addEventListener('click', prevSlide);

    // Navigation par dots
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        currentIndex = index;
        updateCarousel();
      });
    });

    // Auto-play (optionnel)
    let autoPlayInterval = setInterval(nextSlide, 6000);

    // Pause au hover
    carousel.addEventListener('mouseenter', () => {
      if (autoPlayInterval) clearInterval(autoPlayInterval);
    });
    carousel.addEventListener('mouseleave', () => {
      autoPlayInterval = setInterval(nextSlide, 6000);
    });

    // Initialiser
    updateCarousel();
  }

  /**
   * Effet parallaxe sur les images de fond
   */
  initParallax() {
    // Vérifier si l'utilisateur préfère les mouvements réduits
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const parallaxElements = document.querySelectorAll('.grenadiers-parallax-image, .grenadiers-parallax-bg');
    if (parallaxElements.length === 0) return;

    // Désactiver sur mobile pour performance
    if (window.innerWidth <= 768) return;

    let ticking = false;

    const updateParallax = () => {
      const scrollY = window.pageYOffset;

      parallaxElements.forEach(element => {
        const rect = element.getBoundingClientRect();
        const elementTop = rect.top + scrollY;
        const windowHeight = window.innerHeight;

        // Calculer le décalage parallaxe
        const scrolled = scrollY + windowHeight;
        const parallaxSpeed = 0.5; // Ajuster la vitesse (0-1)
        const yPos = -(scrolled - elementTop) * parallaxSpeed;

        element.style.transform = `translate3d(0, ${yPos}px, 0)`;
      });

      ticking = false;
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestTick, { passive: true });
    updateParallax(); // Initialiser
  }

  /**
   * Effet ripple sur les boutons
   */
  initButtonRipple() {
    const buttons = document.querySelectorAll('.grenadiers-btn, button[type="submit"], .add-to-cart-button, .product-form__submit');

    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        const ripple = document.createElement('span');
        const rect = this.getBoundingClientRect();
        const size = Math.max(rect.width, rect.height);
        const x = e.clientX - rect.left - size / 2;
        const y = e.clientY - rect.top - size / 2;

        ripple.style.width = ripple.style.height = size + 'px';
        ripple.style.left = x + 'px';
        ripple.style.top = y + 'px';
        ripple.classList.add('grenadiers-ripple');
        ripple.style.position = 'absolute';
        ripple.style.borderRadius = '50%';
        ripple.style.background = 'rgba(255, 255, 255, 0.6)';
        ripple.style.pointerEvents = 'none';
        ripple.style.transform = 'scale(0)';
        ripple.style.animation = 'ripple-animation 0.6s ease-out';
        ripple.style.zIndex = '1000';

        // Supprimer l'ancien ripple s'il existe
        const oldRipple = this.querySelector('.grenadiers-ripple');
        if (oldRipple) oldRipple.remove();

        this.style.position = 'relative';
        this.style.overflow = 'hidden';
        this.appendChild(ripple);

        // Supprimer après animation
        setTimeout(() => {
          if (ripple.parentNode) {
            ripple.remove();
          }
        }, 600);
      });
    });

    // Ajouter l'animation CSS pour le ripple
    if (!document.getElementById('grenadiers-ripple-style')) {
      const style = document.createElement('style');
      style.id = 'grenadiers-ripple-style';
      style.textContent = `
        @keyframes ripple-animation {
          to {
            transform: scale(4);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Animations supplémentaires sur les cartes produits
   */
  initProductCardAnimations() {
    const productCards = document.querySelectorAll('.product-card, .grenadiers-product-card');

    productCards.forEach(card => {
      // Ajouter un effet de shine au hover
      card.addEventListener('mouseenter', function() {
        this.classList.add('grenadiers-shine');
      });

      card.addEventListener('mouseleave', function() {
        this.classList.remove('grenadiers-shine');
      });
    });
  }
}

// Initialiser les animations
new GrenadiersAnimations();

// Exporter pour utilisation externe si nécessaire
if (typeof module !== 'undefined' && module.exports) {
  module.exports = GrenadiersAnimations;
}
